import type { RollHistoryEntry } from '@dcc/rules/rules/rollHistory.ts';
import { isDamageEntry } from '@dcc/rules/rules/rollHistory.ts';
import { DEGREE_LABELS, type Degree } from '@dcc/rules/rules/degrees.ts';

function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}

/**
 * One roll's full arithmetic, read only from the stored history entry, so an
 * old roll keeps the modifiers it was made with. Ported from the desktop app.
 */
export interface RerollControl {
  /** Null when the Check can be rerolled now; otherwise the button is hidden. */
  problem: string | null;
  aiFavor: number;
  disabled: boolean;
  onReroll: () => void;
}

export function RollBreakdown({ entry, reroll }: { entry: RollHistoryEntry; reroll?: RerollControl }) {
  if (isDamageEntry(entry)) {
    return (
      <div className="dcc-breakdown">
        <div className="dcc-breakdown-title">{entry.label} ({entry.damageType})</div>
        <ul>
          {entry.components.map((c, i) => (
            <li key={`${c.label}-${i}`}>
              <span>{c.label}{c.faces ? ` [${c.faces.join(', ')}]` : ''}</span>
              <span>{c.value}</span>
            </li>
          ))}
          <li className="dcc-breakdown-total"><span>Total</span><span>{entry.total}</span></li>
        </ul>
      </div>
    );
  }

  const bothDice = entry.dice.length > 1;
  return (
    <div className="dcc-breakdown">
      <div className="dcc-breakdown-title">{entry.label}</div>
      <ul>
        {bothDice ? (
          <>
            <li><span>Dice ({entry.mode})</span><span>{entry.dice.join(', ')}</span></li>
            <li><span>Kept</span><span>{entry.keptDie}</span></li>
          </>
        ) : (
          <li><span>d20</span><span>{entry.keptDie}</span></li>
        )}
        {entry.statAbbr !== null && entry.statMod !== null && (
          <li><span>{entry.statAbbr} Mod</span><span>{signed(entry.statMod)}</span></li>
        )}
        {entry.skillName !== null && entry.skillRank !== null && !entry.untrained && (
          <li><span>{entry.skillName} Rank</span><span>{signed(entry.skillRank)}</span></li>
        )}
        {entry.extraComponents.map((c, i) => (
          <li key={`${c.label}-${i}`}><span>{c.label}</span><span>{signed(c.value)}</span></li>
        ))}
        <li className="dcc-breakdown-total"><span>Total</span><span>{entry.total}</span></li>
      </ul>
      {entry.untrained && (
        <p className="dcc-creator-help">Untrained: rolled with Disadvantage and no Ranks (Rules for Survival p.12).</p>
      )}
      {reroll && reroll.problem === null && (
        <div className="dcc-creator-nav">
          <button className="dcc-creator-secondary" disabled={reroll.disabled} onClick={reroll.onReroll}>
            Reroll with AI Favor ({reroll.aiFavor} left)
          </button>
        </div>
      )}
      {entry.difficulty !== null && entry.degree !== null && (
        <p className="dcc-breakdown-degree">
          Difficulty {entry.difficulty}: <strong>{DEGREE_LABELS[entry.degree as Degree] ?? entry.degree}</strong>
        </p>
      )}
    </div>
  );
}
